// 系统内帮助主题配置：按路由路径匹配（前缀匹配，长前缀优先）
// 内容为纯文本步骤说明，随前端版本发布，无需后台维护
export interface HelpTopic {
  /** 路由路径前缀（如 /sales-orders 命中 /sales-orders、/sales-orders/123） */
  match: string
  title: string
  steps: string[]
  /** 相关入口（显示为跳转按钮） */
  links?: { label: string; path: string }[]
}

export const helpTopics: HelpTopic[] = [
  {
    match: '/ai-growth',
    title: 'AI获客',
    steps: [
      'AI获客按租户版本提供（平台在版本上勾选开通），包含"AI 文案助手"和"AI 客户唤醒"两个工具。',
      'AI 文案助手：选场景（朋友圈/抖音脚本/促销/新品/关怀/卖点）→ 可选关联商品 → 一键生成多条文案，复制后即可发布。',
      'AI 客户唤醒：按 沉睡（90 天未成交）/ 高价值 / 欠款 三个分群筛出客户，点"生成话术"得到个性化跟进话术，可直接跳客户详情去跟进。',
      '欠款口径与财务对账一致；AI 生成内容发布/发送前请人工审核。',
      'AI 调用按量计费（从 AI 账户余额扣），余额不足时请先充值。'
    ],
    links: [{ label: 'AI助手', path: '/ai-chat' }]
  },
  {
    match: '/ai-chat',
    title: 'AI助手（AI 开单/问数）',
    steps: [
      'AI 开单：用"秦丝智能导入"拍一张上游单据截图，AI 自动识别商品生成单据；语音/文字说一句话也能创建跟进、待办、工单。',
      'AI 问数：直接用大白话问"上月销售额""谁欠款最多"，AI 实时查系统数据回答。',
      '语音输入：输入框旁的录音按钮可语音转文字（需管理员配置 ASR）。',
      'AI 按量计费：系统设置 → AI 助手设置 可查看余额、消费流水并充值。'
    ],
    links: [{ label: 'AI获客', path: '/ai-growth' }]
  },
  {
    match: '/system/setup-wizard',
    title: '期初建账向导',
    steps: [
      '按 ①仓库与分类 → ②商品资料 → ③库存期初 → ④客户与供应商 → ⑤资金账户期初 → ⑥员工与用户 的顺序完成初始化。',
      '基础资料建议用 Excel 批量导入：先点"下载模板"，按模板列填写后再导入。',
      '库存期初是覆盖式导入：以文件实盘数量为准自动调账，同一文件重复导入不会翻倍。',
      '资金账户在"财务管理"页建立后，编辑账户把余额改成当前真实余额即完成期初校准（系统自动留痕）。'
    ]
  },
  {
    match: '/system/import-export',
    title: '导入导出',
    steps: [
      '模块列表每行提供 导入 / 导出 / 模板 三个操作。',
      '导入前务必先下载模板，严格按模板列名填写；商品按"编码"匹配去重。',
      '商品导入支持撤回：导入记录在页面下方，点"撤回"可整批撤销。',
      '单据导入（秦丝/管家婆等来源）用顶部"打开导入向导"，支持字段映射和模拟导入。'
    ]
  },
  {
    match: '/sales-orders',
    title: '销售订单',
    steps: [
      '新建订单：选择客户 → 添加商品行（数量、单价）→ 保存草稿。',
      '草稿需"提交"→"审核"后才会生成待出库单；不影响绩效的单据审核后直接完成、不出库。',
      '审核通过后在"出库管理"确认出库，库存才真正扣减。',
      '换货在报价单中建立（换货单），退订=只换出不换入的换货单。',
      '订单金额口径：应收 = 订单金额 + 另收运费 − 退货 − 已收 + 已退。'
    ],
    links: [
      { label: '新建销售订单', path: '/sales-orders/create' },
      { label: '出库管理', path: '/inventory/outbounds' }
    ]
  },
  {
    match: '/quotations',
    title: '报价管理',
    steps: [
      '报价单可直接转销售单（客户接受后）。',
      '换货单也在此建立：选择原销售单，添加换出/换入行，客户接受后自动同步销售单。',
      '已接受的换货单如需撤销，仅管理员可操作，系统会完整逆向库存与提成。'
    ]
  },
  {
    match: '/inventory/outbounds',
    title: '出库管理',
    steps: [
      '待出库单由销售订单审核自动生成，确认出库后扣减库存并计提提成。',
      '确认出库时可实填运费：公司承担计入费用，向客户另收计入该单应收。',
      '同客户同仓库的多个待出库单可合并一次出库。',
      '已出库的单如需回退，使用"撤销出库"，库存自动回补。'
    ]
  },
  {
    match: '/inventory/inbounds',
    title: '入库管理',
    steps: [
      '入库单由采购单收货或退货审核自动生成，也可手工新建。',
      '确认入库时实填运费会自动生成运费报销单，并关联对应采购单。'
    ]
  },
  {
    match: '/inventory/stock',
    title: '库存查询',
    steps: [
      '可用库存 = 现货 + 采购在途 − 销售待出。',
      '支持按仓库/库位/商品筛选；开启库位功能后可精确到库位。'
    ]
  },
  {
    match: '/inventory/stocktakes',
    title: '库存盘点',
    steps: [
      '新建盘点单 → 录入实盘数量 → 审核后自动调账，差异生成盘盈入库/盘亏出库凭证。',
      '盘点单支持"回退"，可整体撤销本次调账。'
    ]
  },
  {
    match: '/returns',
    title: '退货管理',
    steps: [
      '退货流程：新建（挂源销售单）→ 提交 → 验货 → 审核。',
      '销售退货审核后自动回补库存、冲减应收，并对销售成员生成负数提成。',
      '已收款订单退款在审核后点"退款"操作，从资金账户支出。'
    ]
  },
  {
    match: '/finance',
    title: '财务管理',
    steps: [
      '资金账户：现金/银行/微信/支付宝/信用卡，余额变动全部留痕（账户变更日志）。',
      '收款可挂订单核销，也可按客户总额自动分摊旧单；超出部分记预收。',
      '优惠退款：客户已付款后纯折让真退钱，用"优惠退款"按钮（生成双流水，对账自洽）。',
      '账户允许透支：编辑账户开启"允许透支"后，转账/报销/退款不再拦截余额不足。'
    ]
  },
  {
    match: '/finance/reconciliations',
    title: '客户对账',
    steps: [
      '对账口径：应收 = 订单金额 + 另收运费 − 退货；已收含优惠核销；已退含退货退款与优惠退款。',
      '支持按时间段导出对账单发给客户核对。'
    ]
  },
  {
    match: '/customers',
    title: '客户管理',
    steps: [
      '手机号是客户唯一标识，重复导入/新建会被拦截。',
      '客户详情页可见应收、购买记录、利润贡献等完整画像。',
      '重复客户档案可用"合并"功能归并（仅管理员），业务单据自动转移。'
    ],
    links: [{ label: '新建客户', path: '/customers/create' }]
  },
  {
    match: '/products',
    title: '商品管理',
    steps: [
      '商品编码全局唯一，是导入匹配的唯一依据。',
      '支持拼音/首字母搜索（如"吊灯"可输入 diaodeng 或 dd）。',
      '"打印标签"可打印价格标签（需先连接 hiprint 本地打印客户端）。'
    ],
    links: [{ label: '新建商品', path: '/products/create' }]
  },
  {
    match: '/reports',
    title: '报表中心',
    steps: [
      '销售报表与采购报表分两个入口；日期口径统一为业务日期（单据日期优先）。',
      '毛利 = 销售金额 − 出库成本快照（移动加权平均成本）。',
      '无敏感权限的账号看不到进价/成本/毛利列。'
    ]
  },
  {
    match: '/dashboard',
    title: '工作台',
    steps: [
      '顶部可签到/签退，外勤打卡会记录定位。',
      '管理员初次使用请先完成"期初建账向导"（系统管理 → 建账向导）。',
      '右上角问号图标可随时打开本帮助。'
    ]
  }
]

// 按当前路由匹配帮助主题：长前缀优先，/sales-orders/create 命中 /sales-orders
export function matchHelpTopic(path: string): HelpTopic | null {
  const sorted = [...helpTopics].sort((a, b) => b.match.length - a.match.length)
  for (const t of sorted) {
    if (path === t.match || path.startsWith(t.match + '/') || path.startsWith(t.match)) return t
  }
  return null
}
